import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Users, Mail, Phone, Bell, Lock } from 'lucide-react';

const INTERNAL_ROLES = [
  "Director",
  "Project Director",
  "Project Manager",
  "Contracts Manager",
  "Commercial Manager",
  "Quantity Surveyor",
  "Planner",
  "Construction Manager",
  "Site Agent",
  "Site Manager",
  "Site Engineer",
  "Foreman",
  "Procurement Manager",
  "Plant Manager",
  "Stores Manager",
  "Document Controller",
  "Quality Manager",
  "Safety Officer",
  "Project Administrator",
  "Other"
];

const emptyForm = {
  name: '',
  surname: '',
  position: '',
  department: '',
  email: '',
  mobile: '',
  role: '',
  include_in_all_notices: false,
  internal_only: true
};

export default function InternalTeamManager({ projectId, canEdit = true }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    loadMembers();
  }, [projectId]);

  const loadMembers = async () => {
    try {
      const response = await api.internalTeam.getAll(projectId);
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load internal team');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingMember) {
        await api.internalTeam.update(projectId, editingMember.id, form);
        toast.success('Team member updated');
      } else {
        await api.internalTeam.create(projectId, form);
        toast.success('Team member added');
      }

      setDialogOpen(false);
      setEditingMember(null);
      setForm(emptyForm);
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save team member');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setForm({
      name: member.name || '',
      surname: member.surname || '',
      position: member.position || '',
      department: member.department || '',
      email: member.email || '',
      mobile: member.mobile || '',
      role: member.role || '',
      include_in_all_notices: member.include_in_all_notices || false,
      internal_only: member.internal_only !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (memberId) => {
    if (!confirm('Are you sure you want to delete this team member?')) return;

    try {
      await api.internalTeam.delete(projectId, memberId);
      toast.success('Team member deleted');
      loadMembers();
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  const openAddDialog = () => {
    setEditingMember(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const filteredMembers = filterRole === 'all' 
    ? members 
    : members.filter(m => m.role === filterRole);

  const groupedMembers = filteredMembers.reduce((acc, member) => {
    const role = member.role || 'Other';
    if (!acc[role]) acc[role] = [];
    acc[role].push(member);
    return acc;
  }, {});

  if (loading) {
    return <div className="p-4 text-slate-500">Loading internal team...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-barlow font-bold uppercase tracking-tight">Internal Team Directory</h3>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-[200px] rounded-sm">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {INTERNAL_ROLES.map(r => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canEdit && (
          <Button 
            onClick={openAddDialog}
            data-testid="add-internal-member-button"
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-2"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Team Member
          </Button>
        )}
      </div>

      <div className="text-sm text-slate-500 mb-4">
        {members.length} team member(s) registered
      </div>

      {Object.keys(groupedMembers).length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-8 text-center">
          <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">No internal team members added yet.</p>
          {canEdit && (
            <Button onClick={openAddDialog} variant="outline" className="mt-4">
              Add First Team Member
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedMembers).map(([role, roleMembers]) => (
            <div key={role} className="space-y-2">
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-2">
                {role} ({roleMembers.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {roleMembers.map((member) => (
                  <div 
                    key={member.id} 
                    className="bg-white border border-slate-200 rounded-sm p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h5 className="font-bold text-slate-900">{member.name} {member.surname}</h5>
                        <p className="text-sm text-slate-600">{member.position}</p>
                        {member.department && (
                          <p className="text-xs text-slate-500">{member.department}</p>
                        )}
                      </div>
                      <div className="flex gap-1 flex-wrap justify-end">
                        {member.include_in_all_notices && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            <Bell className="h-2 w-2 mr-1" />
                            All Notices
                          </Badge>
                        )}
                        {member.internal_only && (
                          <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600 border-slate-300">
                            <Lock className="h-2 w-2 mr-1" />
                            Internal
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="space-y-1 text-sm">
                      {member.email && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="h-3 w-3" />
                          <span className="truncate">{member.email}</span>
                        </div>
                      )}
                      {member.mobile && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="h-3 w-3" />
                          <span>{member.mobile}</span>
                        </div>
                      )}
                    </div>

                    {canEdit && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                        <Button
                          onClick={() => handleEdit(member)}
                          variant="ghost"
                          size="sm"
                          className="text-slate-600 hover:text-slate-900"
                        >
                          <Edit2 className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleDelete(member.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">
              {editingMember ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Name *
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Surname *
                </Label>
                <Input
                  value={form.surname}
                  onChange={(e) => setForm({ ...form, surname: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Position *
                </Label>
                <Input
                  value={form.position}
                  onChange={(e) => setForm({ ...form, position: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Department
                </Label>
                <Input
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Role *
              </Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {INTERNAL_ROLES.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Email *
                </Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Mobile
                </Label>
                <Input
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            </div>

            {/* Distribution Settings */}
            <div className="space-y-4 bg-blue-50 p-4 rounded-sm">
              <h4 className="text-sm font-bold uppercase tracking-wider text-blue-700">Distribution Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="include-all-notices"
                    checked={form.include_in_all_notices}
                    onCheckedChange={(checked) => setForm({ ...form, include_in_all_notices: checked })}
                  />
                  <Label htmlFor="include-all-notices" className="text-sm cursor-pointer">
                    Include in All Notices
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="internal-only"
                    checked={form.internal_only}
                    onCheckedChange={(checked) => setForm({ ...form, internal_only: checked })}
                  />
                  <Label htmlFor="internal-only" className="text-sm cursor-pointer">
                    Internal Only (CC instead of TO)
                  </Label>
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={saving || !form.role || !form.name || !form.surname || !form.email}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
            >
              {saving ? 'Saving...' : (editingMember ? 'Update Team Member' : 'Add Team Member')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
